import { AlertTriangle, MailCheck } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@/lib/form";
import { landingPath } from "@/lib/nav";
import { Shell } from "@/routes/invite";

/**
 * Asking for a link to choose a new password (ADR-057 in `platform`).
 *
 * The page's shape follows the route's: **the answer is the same whatever the address is.** It
 * never says "no account with that address", because a form on the sign-in page that does is a
 * staff directory anyone can read. So there is one success screen, the server's own sentence is
 * what it shows, and the only error it can report is not reaching the server at all.
 *
 * A rep who is already signed in has no use for this and goes to their landing page — the one way
 * to arrive here signed in is the browser's back button after a reset.
 */
export function ForgotPasswordPage() {
  const { user, loading } = useAuth();
  const [sent, setSent] = React.useState<string | null>(null);

  if (loading) return null;
  if (user) return <Navigate to={landingPath(user.role)} replace />;

  if (sent !== null) {
    return (
      <Shell title="Check your email">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-9 items-center justify-center rounded-full bg-accent-3 text-accent-11">
            <MailCheck className="size-4.5" aria-hidden />
          </div>
          <p className="text-sm text-text">{sent}</p>
          <p className="text-xs text-text-muted">
            The link works once and expires in an hour. If nothing arrives, check the spam folder,
            then ask an admin.
          </p>
          <Button variant="outline" className="mt-1 w-full" asChild>
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Reset your password" subtitle="We will email you a link to choose a new one.">
      <RequestForm onSent={setSent} />
    </Shell>
  );
}

const schema = z.object({
  email: z.string().min(1, "Enter your email address").email("That does not look like an email"),
});
type FormValues = z.infer<typeof schema>;

function RequestForm({ onSent }: { onSent: (message: string) => void }) {
  const [formError, setFormError] = React.useState<string | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const answer = await api.publicPost<{ message: string }>("/auth/password-reset", {
        email: values.email,
      });
      onSent(answer.message);
    } catch (e) {
      // The route answers the same way for every address, so anything that reaches here is the
      // request not landing rather than something about the account.
      setFormError(
        e instanceof ApiError ? e.message : "Could not reach the server. Try again.",
      );
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {formError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-danger-4 bg-danger-2 px-3 py-2 text-xs text-danger-11"
        >
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{formError}</span>
        </div>
      )}

      <Field
        label="Email"
        htmlFor="email"
        hint="The address you sign in with."
        error={form.formState.errors.email?.message}
      >
        <Input
          type="email"
          autoComplete="username"
          autoFocus
          placeholder="you@aicaseleads.com"
          invalid={Boolean(form.formState.errors.email)}
          {...form.register("email")}
        />
      </Field>

      <Button
        type="submit"
        variant="default"
        size="lg"
        className="w-full"
        loading={form.formState.isSubmitting}
      >
        Email me a link
      </Button>

      <p className="text-center text-xs text-text-subtle">
        <Link to="/login" className="underline-offset-2 hover:underline">
          Back to sign in
        </Link>
      </p>
    </form>
  );
}
